import { ContentfulCaseStudy, ContentfulCaseStudyIndustry } from '@ravago/shared/page-data/models/contentful';
import { OverviewPageCaseStudyItem } from '@ravago/shared/page-data/models/elastic';
import { CaseStudyCard } from '@ravago/shared/page-data/models/elements';

import { AssetUtils } from '../../lib/utils/asset/asset.utils';

export class CaseStudyClientUtils {
  private static getCaseStudyImageFromOverviewProduct(caseStudy: OverviewPageCaseStudyItem) {
    if (caseStudy.images?.length) return caseStudy.images[0];
    if (!caseStudy.industries) return undefined;

    for (let i = 0; i < caseStudy.industries.length; i += 1) {
      const industry = caseStudy.industries[i];
      if (industry.imageURL) {
        return industry.imageURL;
      }
      for (let j = 0; j < industry.segments.length; j += 1) {
        const segment = industry.segments[j];
        if (segment.imageURL) return segment.imageURL;
      }
    }

    return undefined;
  }

  private static getMostCommonMarket = (caseStudy: ContentfulCaseStudy): ContentfulCaseStudyIndustry | undefined => {
    const industries = caseStudy.segments?.map((segment) => segment.industry).filter((item) => item);

    if (!industries.length) return undefined;

    return industries.sort((a, b) => industries.filter((v) => v === a).length - industries.filter((v) => v === b).length).pop();
  };

  private static getCaseStudyCardImage(
    caseStudy: ContentfulCaseStudy | OverviewPageCaseStudyItem,
    assetBasePath: string,
    acceptWebp?: boolean
  ): {
    image: string;
    backgroundColor?: 'primary' | 'secondary';
  } {
    if ('qualifier' in caseStudy) {
      const imageUrl = this.getCaseStudyImageFromOverviewProduct(caseStudy);
      if (imageUrl) return { image: AssetUtils.optimizeImageIfRequired(imageUrl, acceptWebp, 400) };
      return { image: `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`, backgroundColor: 'primary' };
    }

    if (caseStudy.image?.url) return { image: AssetUtils.optimizeImageIfRequired(caseStudy.image.url, acceptWebp, 400) };
    if (caseStudy.images?.[0]?.fields?.file?.url) return { image: AssetUtils.optimizeImageIfRequired(caseStudy.images[0].fields.file.url, acceptWebp, 400) };

    const industry = this.getMostCommonMarket(caseStudy);
    const fallbackBanner = `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`;

    if (!industry) return { image: fallbackBanner, backgroundColor: 'primary' };

    return { image: AssetUtils.optimizeImageIfRequired(industry.image.url, acceptWebp, 400) };
  }

  static transformToCaseStudyCard(toTransform: ContentfulCaseStudy | OverviewPageCaseStudyItem, assetBasePath: string, acceptWebp?: boolean): CaseStudyCard {
    const caseStudyCard: CaseStudyCard = {
      id: '',
      slug: toTransform.slug,
      title: '',
      excerpt: toTransform.excerpt,
      markets: [],
      products: [],
      isConfidential: false,
      ...this.getCaseStudyCardImage(toTransform, assetBasePath, acceptWebp)
    };

    if ('qualifier' in toTransform) {
      caseStudyCard.id = toTransform.slug;
      caseStudyCard.title = toTransform.label;
      caseStudyCard.markets = toTransform.industries?.map(({ name }) => name);
      caseStudyCard.products = toTransform.products?.map((product) => ({ name: product.name, pristine: false })) ?? [];
      caseStudyCard.isConfidential = toTransform.confidential;
      caseStudyCard.visibility = toTransform.visibility;
    } else {
      caseStudyCard.id = toTransform.sys.id;
      caseStudyCard.title = toTransform.title;
      caseStudyCard.markets = toTransform.segments.map(({ industry }) => industry.name);
      caseStudyCard.products = toTransform.products.map((product) => ({
        name: product.name,
        pristine: false
      }));
      caseStudyCard.isConfidential = toTransform.isConfidential;
      caseStudyCard.visibility = toTransform.visibility;
      caseStudyCard.noFollow = toTransform.seoMetadata?.noFollow;
    }

    return caseStudyCard;
  }
}
